/**
 * Self-service account actions for the signed-in operator (backlog 507): change your own
 * password, and manage your passkeys. These hit the BFF's /api/auth/* routes directly rather
 * than the MCP forwarder, because identity is the console's own concern, not the MCP's.
 *
 * The mutating calls are CSRF-protected the same way mcpWrite is: send the cached token, and
 * if the server says it expired (the session rotated), refetch once and retry.
 */
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { api, ApiError } from './api';
import { getCsrfToken, clearCsrfToken } from './csrf';

export interface PasskeyInfo {
  id: number;
  name: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  deviceType: string | null;
  backedUp: boolean;
}

/** POST/DELETE against /api/auth with the CSRF header, retrying once on an expired token. */
async function csrfSend<T>(path: string, init: RequestInit): Promise<T> {
  const send = (token: string) => api<T>(path, { ...init, headers: { 'x-csrf-token': token, ...(init.headers ?? {}) } });
  try {
    return await send(await getCsrfToken());
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && err.code === 'invalid_csrf_token') {
      clearCsrfToken();
      return send(await getCsrfToken(true));
    }
    throw err;
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await csrfSend<{ ok: true }>('/auth/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const { passkeys } = await api<{ passkeys: PasskeyInfo[] }>('/auth/passkeys');
  return passkeys;
}

/** Enrol another passkey on this account: begin a challenge, run the browser ceremony, finish. */
export async function addPasskey(name: string): Promise<PasskeyInfo[]> {
  const { challengeId, options } = await csrfSend<{ challengeId: string; options: PublicKeyCredentialCreationOptionsJSON }>(
    '/auth/passkey/add/begin',
    { method: 'POST', body: JSON.stringify({}) },
  );
  const response = await startRegistration({ optionsJSON: options });
  const { passkeys } = await csrfSend<{ ok: true; passkeys: PasskeyInfo[] }>('/auth/passkey/add/finish', {
    method: 'POST',
    body: JSON.stringify({ challengeId, response, name: name || null }),
  });
  return passkeys;
}

export async function removePasskey(id: number): Promise<PasskeyInfo[]> {
  const { passkeys } = await csrfSend<{ ok: true; passkeys: PasskeyInfo[] }>(`/auth/passkeys/${id}`, { method: 'DELETE' });
  return passkeys;
}
